import cn from 'classnames';
import { Field } from 'redux-form';
import connect from 'components/Shared/connect';
import { NO_ELEMENT, rm } from 'utils';
import * as rules from 'views/CheckoutPage/validation';
import { RenderCheckbox, RenderLabeledInput } from 'components/Shared/Form/inputs';
import {
  fieldStreet,
  fieldZipcode,
  selectDisableMessageBoxFeatureToggle
} from 'views/CheckoutPage/selector';
import ContactInfoPolicy from 'views/CheckoutPage/PolicyInformation/themes/BGH-RD3/contactInfoPolicy';
import ManualFormWrapper, { RenderFormProps } from '../manualForm';
import { postAddressSuggestions, postZipcodeSuggestions } from 'services/ApiCheckoutPageService';
import SuggestionField, { addressFormat, zipFormat, zipParser } from '../suggestionField';
import { ToggleQuickstart } from '../toggleQuickstart';
import { LeaveMessage } from '../leaveMessage';
import RotForm from './rotForm';
import commonStyles from '../styles.scss';
import { CertificateForm } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/certificateForm';
import { RentalForm } from '../rentalForm';
import { debounceP } from 'utils/debounce';
import { useSelector } from 'react-redux';
import { selectNameMaxLength } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/selector';

interface OwnProps {
  toggleQuickStart: () => void;
}

interface MergedProps extends OwnProps {
  hasRotDeduction: boolean;
  shouldDisableMessageBoxFeatureToggle: boolean;
}

type InternalRenderFormProps = MergedProps & {
  maxNameLength?: number;
};

const SUGGESTION_DEBOUNCE_TIME = 250;

const renderForm =
  ({
    hasRotDeduction,
    toggleQuickStart,
    shouldDisableMessageBoxFeatureToggle,
    maxNameLength
  }: InternalRenderFormProps) =>
  (props: RenderFormProps) => {
    const onAddressChange = (street: string) => {
      props.resetAddressValidation();
      return postAddressSuggestions(street);
    };
    const onZipCodeChange = (zipCode: string) => {
      props.resetAddressValidation();
      return postZipcodeSuggestions(zipCode);
    };

    const debouncedAddressChange = debounceP(onAddressChange, SUGGESTION_DEBOUNCE_TIME);
    const debouncedZipCodeChange = debounceP(onZipCodeChange, SUGGESTION_DEBOUNCE_TIME);

    return (
      <div>
        <ToggleQuickstart
          label={rm(
            'checkout_contact_info_address_automatic',
            rm('checkout_identityheader_private').toLowerCase()
          )}
          onClick={toggleQuickStart}
          className={commonStyles.toggleManual}
        />
        <Field
          name="firstName"
          validate={[rules.name, rules.nameLength(maxNameLength)]}
          className={cn(commonStyles.input, commonStyles.left)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_firstname') }}
        />
        <Field
          name="lastName"
          validate={[rules.name, rules.nameLength(maxNameLength)]}
          className={cn(commonStyles.input, commonStyles.right)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_lastname') }}
        />
        <SuggestionField
          name="street"
          className={commonStyles.input}
          validate={rules.address}
          label={rm('checkout_address')}
          onBlur={props.onBlur}
          onChange={debouncedAddressChange}
          onSelect={props.suggestionSelected(fieldStreet)}
          format={addressFormat}
          props={{
            customValid: props.hasValidAddress
          }}
        />
        <SuggestionField
          name="zipCode"
          className={cn(commonStyles.input, commonStyles.left)}
          validate={rules.zip}
          label={rm('checkout_zipcode')}
          onBlur={props.onBlur}
          onChange={debouncedZipCodeChange}
          onSelect={props.suggestionSelected(fieldZipcode)}
          format={zipFormat}
          props={{
            inputMode: 'numeric',
            customValid: props.hasValidAddress || props.hasValidZipCode
          }}
          parse={zipParser}
        />
        <Field
          name="city"
          validate={rules.city}
          className={cn(commonStyles.input, commonStyles.right)}
          onBlur={props.onBlur}
          onChange={props.resetAddressValidation}
          component={RenderLabeledInput}
          props={{
            customLabel: rm('checkout_city'),
            customValid: props.hasValidAddress || props.hasValidZipCode
          }}
        />
        <Field
          name="hasDeliveryAddress"
          onChange={props.deliveryAddressChanged}
          component={RenderCheckbox}
          label={rm('checkout_delivery_alternative_label')}
          className={commonStyles.checkboxWrapper}
        />
        <Field
          name="phoneNumber"
          type="tel"
          tooltip={rm('checkout_cellphone_help')}
          validate={rules.phone}
          className={cn(commonStyles.input, commonStyles.left)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_cellphone') }}
          inputMode="tel"
        />
        <Field
          name="email"
          type="email"
          validate={rules.email}
          className={cn(commonStyles.input, commonStyles.right)}
          onBlur={props.onBlur}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_email_rd3') }}
        />
        {shouldDisableMessageBoxFeatureToggle ? (
          NO_ELEMENT
        ) : (
          <LeaveMessage className={commonStyles.message} onBlur={props.onBlur} />
        )}
        {hasRotDeduction && <RotForm isManual onBlur={props.onBlur} />}
        <RentalForm isManual onBlur={props.onBlur} />
        <CertificateForm />
        <ContactInfoPolicy />
      </div>
    );
  };

const ManualForm = (props: MergedProps) => {
  const maxNameLength = useSelector(selectNameMaxLength);

  return (
    <ManualFormWrapper
      formType="primary"
      renderForm={renderForm({ ...props, maxNameLength: maxNameLength })}
    />
  );
};

export default connect<MergedProps, OwnProps>(state => ({
  hasRotDeduction: state.cart.hasRotDeduction,
  shouldDisableMessageBoxFeatureToggle: selectDisableMessageBoxFeatureToggle(state)
}))(ManualForm);
